import {Component, DoCheck, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {ProfileLayoutComponent} from '../profile-layout.component';
import {ActivatedRoute} from '@angular/router';
import {finalize, takeUntil} from 'rxjs/operators';
import * as aux from 'adcore-auxiliary';
import * as cfg from '../../helpers/config';
import {lastValueFrom, timer} from 'rxjs';
import {MessageService} from 'primeng/api';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
declare var grecaptcha: any;

// /join?uit=<token>: an invited user completes the sign-up (name, phone, website, password, phone code).
// /join without an invitation is redirected to the Media Blast self-onboarding (ProfileGuard).
@Component({
  selector: 'app-invitation',
  templateUrl: './join.component.html',
  styleUrls: ['./join.component.scss']
})
export class JoinComponent implements OnInit, DoCheck {
  constructor(private route: ActivatedRoute, private elementRef: ElementRef,
              @Inject(ProfileLayoutComponent) public parent: ProfileLayoutComponent,
              private messageService: MessageService) {
  }

  private reCaptchaScoreV3: number | undefined;
  private disableScoreThreshold = 0.7;
  private readonly recaptchaSiteKey = '6LfXPmEpAAAAAAit7aA8xLMOy1nD3ZCcwDTzaqnz';
  private readonly actions = {
    joinInit: 'join_init',
    phone2fa: 'join_phone',
    phoneResend: 'join_phone_resend'
  };
  passwordRequirement = '<div>Password Requirements:</div>' +
    '<div>- At least 8 characters</div>' +
    '<div>- At least 1 uppercase letter</div>' +
    '<div>- At least 1 lowercase letter</div>' +
    '<div>- At least 1 number or special character (e.g., @, !, #)</div>';

  passwordCriteria = {
    minLength: false,
    uppercase: false,
    lowercase: false,
    specialOrNumber: false
  };

  inProcess = false;
  uit: string | undefined;
  currentStage = 'stage2';
  changePhoneStage = false;
  responseError: string | null = null;
  passwordError: string | null = null;
  fullNameError: string | null = null;
  phoneError: string | null = null;
  newPhoneError: string | null = null;
  websiteError: string | null = null;
  @ViewChild('phone') phone: aux.PhoneComponent | undefined;
  @ViewChild('newPhone') newPhone: aux.PhoneComponent | undefined;
  loginInfo: any = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    pass1: '',
    pass2: '',
    websiteUrl: ''
  };
  currentCountryCode = 'US';
  phoneVerifyInfo: any = null;
  resendTimes = 0;
  countdown = cfg.VERIFY_RESEND_SECONDS;
  isResendDisabled = false;
  codeError = false;
  phoneCodeCorrect = false;
  showPasswordFeedback = false;
  phoneCode: any = null;
  fingerprint = '';


  ngDoCheck(): void {
    this.elementRef.nativeElement.querySelectorAll(`input[type='password']`).forEach((el: any) => {
      el.setAttribute('autocomplete', 'new-password');
    });
  }

  checkPasswordStrength(password: string): void {
    this.passwordError = null;
    this.passwordCriteria.minLength = password.length >= 8;
    this.passwordCriteria.uppercase = /[A-Z]/.test(password);
    this.passwordCriteria.lowercase = /[a-z]/.test(password);
    this.passwordCriteria.specialOrNumber = /[0-9\W]/.test(password);
    // Hide the feedback if all criteria are met
    this.showPasswordFeedback = password.length > 0 && !Object.values(this.passwordCriteria).every(criteria => criteria);
  }

  ngOnInit(): void {
    void this.setFingerprint().then(() => this.initJoinSession());
    this.validateReCaptchaV3();
    this.currentCountryCode = this.parent.mediaService.currentCountryCode;
    if (!this.currentCountryCode) {
      const nativeGeoIp = this.parent.winRef.nativeGeoIp;
      if (nativeGeoIp) {
        nativeGeoIp.country((geoipResponse: any) => {
            this.currentCountryCode = geoipResponse.country.iso_code;
          },
          (err: any) => {
            console.log('Error', err);
          });
      }
    }
    this.route.queryParams
      .pipe(takeUntil(this.parent.componentDestroyed))
      .subscribe(params => {
          this.uit = params.uit;
          this.parent.changePlan.emit('all');
          this.parent.changeStage.emit(this.currentStage);
        }
      );
  }

  private async setFingerprint(): Promise<void> {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      const { screenResolution, screenFrame, ...components } = result.components || {};
      this.fingerprint = FingerprintJS.hashComponents(components || ({} as any));
    } catch (error) {
      this.fingerprint = '';
      console.error('Fingerprint error:', error);
    }
  }

  private initJoinSession(): void {
    if (!this.fingerprint) {
      return;
    }
    this.parent.apiCallService.initJoinSession(this.fingerprint)
      .pipe(takeUntil(this.parent.componentDestroyed))
      .subscribe(() => {});
  }

  get fullName(): string {
    return `${this.loginInfo.firstName || ''} ${this.loginInfo.lastName || ''}`.trim();
  }

  private get isPasswordStrengthEnough(): boolean {
    const re = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9\\W])(?=.{8,})');
    return re.test(this.loginInfo.pass1 || '');
  }

  validate(): boolean {
    this.phoneError = null;
    this.passwordError = null;
    this.fullNameError = null;
    this.websiteError = null;
    if (aux.phoneNumberValidator(this.phone)) {
      this.phoneError = 'Please enter valid phone number.';
    }

    const re2 = new RegExp('^(?:http(s)?:\\/\\/)?[\\w.-]+(?:\\.[\\w\\.-]+)+[\\w\\-\\._~:/?#[\\]@!\\$&\'\\(\\)\\*\\+,;=.]+$');
    if (!re2.test(this.loginInfo.websiteUrl || '')) {
      this.websiteError = 'PLease enter valid website url.';
    }

    if (!this.loginInfo.pass1 || !this.loginInfo.pass2) {
      this.passwordError = 'Please enter valid password';
    }
    if (this.loginInfo.pass1 && !this.isPasswordStrengthEnough) {
      this.passwordError = 'Please enter strong password';
    }
    if (this.loginInfo.pass1 && this.loginInfo.pass2 && this.loginInfo.pass1 !== this.loginInfo.pass2) {
      this.passwordError = 'Passwords not identical';
    }
    if (!this.fullName.length) {
      this.fullNameError = 'Please enter valid full name';
    }
    return Boolean(!this.passwordError && !this.fullNameError && !this.phoneError && !this.websiteError);
  }

  removeError(event: string): void {
    if (event === 'website') {
      this.websiteError = null;
    }
  }

  private finalizeAdminInvitation(): void {
    this.inProcess = true;
    this.parent.apiCallService
      .adminInvitationFinalization(this.uit || '', this.loginInfo.firstName || '', this.loginInfo.lastName || '',
        this.loginInfo.pass1 || '', this.loginInfo.pass2 || '', '', this.loginInfo.phoneNumber || '')
      .pipe(takeUntil(this.parent.componentDestroyed))
      .subscribe((response) => {
        const r = response || {};
        if (r.result && r.token) {
          this.parent.mediaService.accessToken = r.token;
          this.parent.appAuthService.authenticate();
          this.parent.navigateToMyCloud();
          return;
        }
        this.responseError = r.error.toString();
        console.error(this.responseError);
        this.inProcess = false;
      }, (reason) => {
        this.inProcess = false;
        this.parent.mediaService.accessToken = null;
        this.responseError = reason.toString();
        console.error(this.responseError);
      });
  }

  join(): void {
    this.responseError = null;
    if (this.validate()) {
      this.loginInfo.phoneNumber = this.phone?.fullPhoneNumber?.internationalNumber || '';
      this.verifyPhone();
    }
  }

  async onCodeChange(event: any): Promise<void> {
    this.codeError = false;
    this.phoneCodeCorrect = false;
    this.responseError = null;
    if (this.phoneCode.length === 6) {
      await this.onApplyingPhoneCode();
    }
  }

  changePhone(): void {
    this.changePhoneStage = !this.changePhoneStage;
  }

  updatePhone(): void {
    this.newPhoneError = null;
    if (aux.phoneNumberValidator(this.newPhone)) {
      this.newPhoneError = 'Please enter valid phone number.';
    } else {
      this.loginInfo.phoneNumber = this.newPhone?.fullPhoneNumber?.internationalNumber || '';
      this.phoneVerifyInfo = {
        identifier: this.newPhone?.fullPhoneNumber.internationalNumber,
        channel: 'sms'
      };
      this.send2faRequest('update');
      this.changePhoneStage = false;
    }
  }

  async onApplyingPhoneCode(): Promise<void> {
    this.codeError = false;
    this.phoneCodeCorrect = false;
    this.responseError = null;
    this.inProcess = true;
    if (!this.phoneCode || this.phoneCode?.length < 6) {
      this.codeError = true;
      this.inProcess = false;
      return;
    }
    if (!this.phoneVerifyInfo) {
      this.phoneVerifyInfo = {
        identifier: this.loginInfo.phoneNumber,
        channel: 'sms'
      };
    }
    this.phoneVerifyInfo.code = this.phoneCode;
    this.parent.apiCallService.apiCheck2faVerification(this.phoneVerifyInfo)
      .pipe(takeUntil(this.parent.componentDestroyed))
      .subscribe((response) => {
        if (response.status === 'approved') {
          this.phoneCodeCorrect = true;
          setTimeout(() => {
            this.finalizeAdminInvitation();
          }, 1000);
        } else {
          this.codeError = true;
          this.inProcess = false;
        }
      });
  }

  private validateReCaptchaV3(): void {
    this.reCaptchaScoreV3 = undefined;
    grecaptcha.enterprise.ready(() => {
      grecaptcha.enterprise.execute(this.recaptchaSiteKey, { action: this.actions.joinInit })
        .then(async (token: string) => {
          const { score } = await lastValueFrom(this.parent.apiCallService.validateCaptcha(token, this.actions.joinInit));
          this.reCaptchaScoreV3 = score;
        })
        .catch((error: any) => {
          console.error('Error generating token or validating captcha:', error);
        });
    });
  }

  private async getRecaptchaToken(action: string): Promise<string | null> {
    if (!grecaptcha || !grecaptcha.enterprise || !grecaptcha.enterprise.execute) {
      return null;
    }
    try {
      await new Promise<void>((resolve) => grecaptcha.enterprise.ready(resolve));
      return await grecaptcha.enterprise.execute(this.recaptchaSiteKey, { action });
    } catch (error) {
      console.error('Error generating reCAPTCHA token:', error);
      return null;
    }
  }

  verifyPhone(): void {
    this.phoneVerifyInfo = {
      identifier: this.loginInfo.phoneNumber,
      channel: 'sms'
    };
    this.send2faRequest('init');
  }

  async send2faRequest(verifyType: 'init' | 'update' | 'resend'): Promise<void> {
    this.inProcess = true;
    this.codeError = false;
    this.phoneCodeCorrect = false;
    this.responseError = null;
    this.phoneCode = null;
    const recaptchaAction = verifyType === 'resend' ? this.actions.phoneResend : this.actions.phone2fa;
    const recaptchaToken = await this.getRecaptchaToken(recaptchaAction);
    if (!recaptchaToken) {
      this.inProcess = false;
      this.messageService.add({
        key: '2faMessage',
        severity: 'error',
        closable: false,
        detail: 'reCAPTCHA validation failed. Please try again.'
      });
      return;
    }
    const userInfo = {
      verifyInfo: this.phoneVerifyInfo,
      user: this.loginInfo,
      twilioAction: recaptchaAction,
      recaptchaToken,
      recaptchaAction,
      recaptchaVersion: 'V3',
      fingerprint: this.fingerprint
    };
    this.parent.apiCallService.apiJoin2faVerification(userInfo)
      .pipe(takeUntil(this.parent.componentDestroyed),
      finalize(() => {
        this.inProcess = false;
      })
      )
      .subscribe((response) => {
        if (!response.error) {
          this.phoneVerifyInfo = response;
          const detail = `We’ve sent a verification code to your ${verifyType === 'update' ? 'updated ' : ''}phone number.`;
          if (verifyType === 'update') {
            this.resendTimes = 0;
          }
          this.messageService.add({
            key: '2faMessage',
            severity: 'success',
            closable: false,
            detail});
          this.resendTimes++;
          this.currentStage = 'stage2-verifyPhone';
          this.startCountdown(cfg.VERIFY_RESEND_SECONDS);
        } else {
          let detailMsg = response.error || 'An error occur when sending the verification code!';
          if (response.retryAfter) {
            const retryDate = new Date(response.retryAfter).toLocaleString();
            detailMsg += ` You can try again after ${retryDate}.`;
          }
          this.messageService.add({
            key: '2faMessage',
            severity: 'error',
            closable: false,
            detail: detailMsg});
        }
      });
  }

  startCountdown(seconds: number): void {
    this.isResendDisabled = true;
    this.countdown = seconds;

    const countdownTimer = timer(0, 1000).subscribe(() => {
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        this.isResendDisabled = false;
        countdownTimer.unsubscribe(); // Stop the timer
      }
    });
  }

  get failedReCaptchaV3(): boolean {
    return !this.reCaptchaScoreV3 || this.reCaptchaScoreV3 < this.disableScoreThreshold;
  }

}
